import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Retorna o slug do CMS atual a partir da URL ou do localStorage.
 * Usado para redirecionar corretamente para /{slug}/login em vez de /login.
 */
export function getCurrentSlug(): string {
  try {
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      return pathParts[0];
    }
  } catch (_) {}
  try {
    const authStorage = localStorage.getItem('auth-storage');
    if (authStorage) {
      const parsed = JSON.parse(authStorage);
      const panel = parsed?.state?.user?.panel || parsed?.user?.panel;
      if (panel) return panel;
    }
  } catch (_) {}
  return '';
}

/**
 * Redireciona para o login do CMS correto preservando o slug.
 * Evita redirecionar para /login sem slug que leva ao CmsSelector.
 */
export function redirectToLogin(): void {
  const slug = getCurrentSlug();
  window.location.href = slug ? `/${slug}/login` : '/';
}

/**
 * Converte um valor de data para um objeto Date válido
 * Suporta: string ISO, timestamp Unix (segundos ou milissegundos), Date, null, undefined
 * Retorna null se a data for inválida
 */
export function parseDate(dateValue: string | number | Date | null | undefined): Date | null {
  if (dateValue === null || dateValue === undefined) {
    return null;
  }
  
  try {
    // Se já for um Date, validar e retornar
    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) {
        return null;
      }
      return dateValue;
    }
    
    // Se for string
    if (typeof dateValue === 'string') {
      // String vazia ou apenas espaços
      const trimmed = dateValue.trim();
      if (!trimmed || trimmed === 'null' || trimmed === 'undefined' || trimmed === '') {
        return null;
      }
      
      // Se a string for um número (timestamp como string), converter primeiro
      const numValue = Number(trimmed);
      if (!isNaN(numValue) && isFinite(numValue) && trimmed === String(numValue)) {
        // É um número válido como string, tratar como timestamp
        if (numValue > 0) {
          const timestamp = numValue < 10000000000 ? numValue * 1000 : numValue;
          const date = new Date(timestamp);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }
      
      // Tentar criar Date da string (ISO, formato legível, etc.)
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    }
    
    // Se for número (timestamp)
    if (typeof dateValue === 'number') {
      // Valores inválidos
      if (!isFinite(dateValue)) {
        return null;
      }
      
      // Timestamp 0 ou negativo é inválido
      if (dateValue <= 0) {
        return null;
      }
      
      // Se for menor que 1 trilhão (10^12), provavelmente é segundos, senão é milissegundos
      // Timestamp em segundos: exemplo 1735689600 (janeiro 2025) 
      // Timestamp em milissegundos: exemplo 1735689600000
      const timestamp = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
      
      // Validar que o timestamp está em um range razoável
      // Aceitar datas desde 1970 até 2100 (aprox)
      // Mas ser mais flexível - apenas verificar se não é NaN
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return null;
      }
      
      // Validar apenas se não for uma data claramente inválida (antes de 1970 ou muito futura)
      const dateTime = date.getTime();
      const minReasonable = new Date('1970-01-01').getTime();
      const maxReasonable = new Date('2100-01-01').getTime();
      
      // Se estiver dentro do range razoável, aceitar
      if (dateTime >= minReasonable && dateTime <= maxReasonable) {
        return date;
      }
      
      // Se estiver fora do range, pode ser válido mas retornar null por segurança
      return null;
    }
    
    return null;
  } catch (error) {
    // Em caso de erro, retornar null silenciosamente (não logar para não poluir console)
    return null;
  }
}

/**
 * Converte uma data para string ISO de forma segura
 * Retorna null se a data for inválida
 */
export function toISOStringSafe(dateValue: string | number | Date | null | undefined): string | null {
  const date = parseDate(dateValue);
  if (!date) {
    return null;
  }
  
  try {
    return date.toISOString();
  } catch (error) {
    console.error('Erro ao converter data para ISO:', error, dateValue);
    return null;
  }
}

export function formatDate(date: string | Date | number | null | undefined): string {
  const dateObj = parseDate(date);
  
  if (!dateObj) {
    return '--/--/----, --:--';
  }
  
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.error('Erro ao formatar data:', error, date);
    return '--/--/----, --:--';
  }
}

/**
 * Formata apenas a data (sem hora)
 */
export function formatDateOnly(date: string | Date | number | null | undefined): string {
  const dateObj = parseDate(date);
  
  if (!dateObj) {
    return '--/--/----';
  }
  
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.error('Erro ao formatar data:', error, date);
    return '--/--/----';
  }
}

/**
 * Formata apenas a hora (sem data)
 */
export function formatTimeOnly(date: string | Date | number | null | undefined): string {
  const dateObj = parseDate(date);
  
  if (!dateObj) {
    return '--:--';
  }
  
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.error('Erro ao formatar hora:', error, date);
    return '--:--';
  }
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount)
}

export function formatNumber(number: number): string {
  return new Intl.NumberFormat('pt-BR').format(number)
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-100'
    case 'inactive':
      return 'text-gray-600 bg-gray-100'
    case 'suspended':
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

export function getStatusText(status: string): string {
  switch (status) {
    case 'active':
      return 'Ativo'
    case 'inactive':
      return 'Inativo'
    case 'suspended':
      return 'Suspenso'
    default:
      return 'Desconhecido'
  }
}
